import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Webhook, Plus, Edit, Trash2, CheckCircle, XCircle, Clock, Zap, X } from 'lucide-react'
import { SupabaseService } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import EmptyState from '../components/EmptyState'
import toast from 'react-hot-toast'

const WebhookManager = () => {
  const { user } = useAuth()
  const [webhooks, setWebhooks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: [] as string[],
    secret: ''
  })

  const availableEvents = [
    'payment.created',
    'payment.completed',
    'payment.failed',
    'payment.cancelled',
    'refund.created',
    'refund.completed'
  ]

  useEffect(() => {
    loadWebhooks()
  }, [user?.id])

  const loadWebhooks = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      const webhooksData = await SupabaseService.getWebhooks(user.id)
      setWebhooks(webhooksData)
    } catch (error) {
      console.error('Erreur chargement webhooks:', error)
      toast.error('Erreur lors du chargement des webhooks')
      setWebhooks([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddWebhook = async () => {
    if (!user?.id) {
      ErrorHandler.showError('Utilisateur non connecté')
      return
    }

    // Validation des données
    const validationResult = ValidationUtils.validateWebhookData(newWebhook)
    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => ErrorHandler.showError(error))
      return
    }

    try {
      const webhookData = {
        user_id: user.id,
        url: newWebhook.url,
        events: newWebhook.events,
        status: 'active' as const,
        secret: newWebhook.secret || undefined
      }

      const createdWebhook = await SupabaseService.createWebhook(webhookData)
      setWebhooks(prev => [createdWebhook, ...prev])
      
      setNewWebhook({ url: '', events: [], secret: '' })
      setShowAddModal(false)
      ErrorHandler.showSuccess('Webhook ajouté avec succès!')
    } catch (error) {
      const appError = ErrorHandler.handleSupabaseError(error)
      ErrorHandler.showError(appError.message)
    }
  }

  const toggleWebhookStatus = async (id: string) => {
    try {
      const webhook = webhooks.find(w => w.id === id)
      if (!webhook) return

      const newStatus = webhook.status === 'active' ? 'inactive' : 'active'
      
      await SupabaseService.updateWebhook(id, { status: newStatus })
      
      setWebhooks(prev => prev.map(w => 
        w.id === id ? { ...w, status: newStatus } : w
      ))
      
      toast.success('Statut du webhook mis à jour')
    } catch (error) {
      console.error('Erreur mise à jour webhook:', error)
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const deleteWebhook = async (id: string) => {
    try {
      await SupabaseService.deleteWebhook(id)
      setWebhooks(prev => prev.filter(w => w.id !== id))
      toast.success('Webhook supprimé')
    } catch (error) {
      console.error('Erreur suppression webhook:', error)
      toast.error('Erreur lors de la suppression')
    }
  }

  const testWebhook = async (webhook: any) => {
    const toastId = toast.loading('Test du webhook en cours...')
    
    try {
      // Simuler un test de webhook
      const testPayload = {
        event: 'payment.completed',
        data: {
          id: 'pay_test_' + Date.now(),
          amount: 10000,
          currency: 'XOF',
          status: 'completed'
        },
        timestamp: new Date().toISOString()
      }

      const response = await fetch(webhook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'PayFuse-Webhook-Test/1.0'
        },
        body: JSON.stringify(testPayload)
      })

      if (response.ok) {
        toast.success('Webhook testé avec succès!', { id: toastId })
      } else {
        toast.error(`Test échoué: ${response.status} ${response.statusText}`, { id: toastId })
      }
    } catch (error) {
      toast.error('Erreur lors du test du webhook', { id: toastId })
    }
  }

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // État vide si aucun webhook
  if (!loading && webhooks.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Webhooks</h1>
              <p className="text-gray-600">Configurez et surveillez vos endpoints de notification</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Webhook
            </motion.button>
          </div>
        </motion.div>

        <EmptyState
          icon={Webhook}
          title="Aucun webhook configuré"
          description="Les webhooks vous permettent de recevoir des notifications en temps réel sur vos transactions. Configurez votre premier webhook pour commencer."
          actionLabel="Configurer mon Premier Webhook"
          onAction={() => setShowAddModal(true)}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Notifications Temps Réel</h4>
              <p className="text-sm text-blue-800">Recevez des notifications instantanées sur vos paiements</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Sécurisé</h4>
              <p className="text-sm text-green-800">Signatures cryptographiques pour vérifier l'authenticité</p>
            </div>
          </div>
        </EmptyState>

        {/* Modal reste disponible même en état vide */}
        {renderAddModal()}
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des Webhooks</h1>
            <p className="text-gray-600">Configurez et surveillez vos endpoints de notification</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Webhook
          </motion.button>
        </div>
      </motion.div>

      {/* Webhooks List */}
      <div className="grid gap-6">
        {webhooks.map((webhook, index) => (
          <motion.div
            key={webhook.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Webhook className="w-5 h-5 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-lg">{webhook.url}</h3>
                  <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                    webhook.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {webhook.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {webhook.events.map((event: string) => (
                    <span key={event} className="bg-blue-100 text-blue-800 px-2 py-1 text-xs rounded">
                      {event}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Dernière livraison:</span>
                    <p>{webhook.last_delivery ? new Date(webhook.last_delivery).toLocaleString() : 'Jamais'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Succès:</span>
                    <p className="text-green-600">{webhook.delivery_count || 0}</p>
                  </div>
                  <div>
                    <span className="font-medium">Échecs:</span>
                    <p className="text-red-600">{webhook.failure_count || 0}</p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => testWebhook(webhook)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Tester le webhook"
                >
                  <Zap className="w-4 h-4" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => toggleWebhookStatus(webhook.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    webhook.status === 'active'
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-green-600 hover:bg-green-50'
                  }`}
                  title={webhook.status === 'active' ? 'Désactiver' : 'Activer'}
                >
                  {webhook.status === 'active' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => deleteWebhook(webhook.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {renderAddModal()}
    </div>
  )

  function renderAddModal() {
    return (
      <>
        {/* Add Webhook Modal */}
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Nouveau Webhook</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL du Webhook *
                  </label>
                  <input
                    type="url"
                    value={newWebhook.url}
                    onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://yourapp.com/webhooks/payfuse"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Événements à écouter *
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {availableEvents.map(event => (
                      <label key={event} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newWebhook.events.includes(event)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewWebhook(prev => ({ ...prev, events: [...prev.events, event] }))
                            } else {
                              setNewWebhook(prev => ({ ...prev, events: prev.events.filter(e => e !== event) }))
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secret (optionnel)
                  </label>
                  <input
                    type="password"
                    value={newWebhook.secret}
                    onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Secret pour signer les webhooks"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommandé pour sécuriser vos webhooks
                  </p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddWebhook}
                  disabled={!newWebhook.url || newWebhook.events.length === 0}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Créer
                </motion.button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </>
    )
  }
}

export default WebhookManager